import { useState } from 'react';
import { Button, Card } from '@beautyburst/ui';
import { semanticColor, typeStyle } from '@beautyburst/tokens';
import type { CustomerApiClient } from '../api-client.ts';

/**
 * Transactional email stays visible but is not editable. Other preferences are
 * persisted through the customer API; notification delivery is outside this UI.
 */
export function NotificationsScreen({
  preferences,
  client,
}: {
  preferences: Awaited<ReturnType<CustomerApiClient['notifications']['get']>>;
  client: Pick<CustomerApiClient, 'notifications'>;
}) {
  const [values, setValues] = useState(preferences);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const toggle = (
    key:
      | 'transactional_sms'
      | 'transactional_push'
      | 'marketing_email'
      | 'marketing_sms'
      | 'marketing_push'
      | 'back_in_stock_alerts'
      | 'price_drop_alerts'
      | 'after_hours_alerts'
      | 'membership_updates',
  ) => {
    setValues((current) => ({ ...current, [key]: !current[key] }));
    setSaved(false);
    setSaveError(null);
  };
  const save = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const updated = await client.notifications.update({
        transactional_sms: values.transactional_sms,
        transactional_push: values.transactional_push,
        marketing_email: values.marketing_email,
        marketing_sms: values.marketing_sms,
        marketing_push: values.marketing_push,
        back_in_stock_alerts: values.back_in_stock_alerts,
        price_drop_alerts: values.price_drop_alerts,
        after_hours_alerts: values.after_hours_alerts,
        membership_updates: values.membership_updates,
      });
      setValues(updated);
      setSaved(true);
    } catch {
      setSaveError('We could not save your notification preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section aria-labelledby="notifications-title">
      <h1
        id="notifications-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
      >
        Notification preferences
      </h1>
      <form onSubmit={(event) => void (event.preventDefault(), save())}>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary, marginTop: 0 }}>
            Transactional email
          </h2>
          <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary }}>
            Transactional email is used for essential account and service information. It is not a
            marketing preference.
          </p>
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary, marginTop: 0 }}>
            Marketing and alerts
          </h2>
          {(
            [
              ['transactional_sms', 'Transactional text messages'],
              ['transactional_push', 'Transactional push notifications'],
              ['marketing_email', 'Marketing email'],
              ['marketing_sms', 'Marketing text messages'],
              ['marketing_push', 'Marketing push notifications'],
              ['back_in_stock_alerts', 'Back-in-stock alerts'],
              ['price_drop_alerts', 'Price-drop alerts'],
              ['after_hours_alerts', 'After-hours alerts'],
              ['membership_updates', 'Membership updates'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} style={{ display: 'flex', minHeight: '44px', alignItems: 'center' }}>
              <input type="checkbox" checked={values[key]} onChange={() => toggle(key)} />
              {label}
            </label>
          ))}
          <Button type="submit" loading={saving}>
            Save preferences
          </Button>
          {saved ? (
            <p role="status" aria-live="polite">
              Preferences saved.
            </p>
          ) : null}
          {saveError === null ? null : (
            <p role="alert">We could not save your notification preferences. Please try again.</p>
          )}
        </Card>
      </form>
    </section>
  );
}
